import type {
  CollectionStateBase,
  Key,
  Node,
  SingleSelection,
} from '@react-types/shared';
import {useMemo} from 'react';
import {useControlledState} from '../utils';
import type {ListState} from './useListState';
import {useListState} from './useListState';

export interface SingleSelectListProps<T>
  extends CollectionStateBase<T>,
    Omit<SingleSelection, 'disallowEmptySelection'> {
  /** Filter function to generate a filtered list of nodes. */
  filter?: (nodes: Iterable<Node<T>>) => Iterable<Node<T>>;
  /** @private */
  suppressTextValueWarning?: boolean;
}

export interface SingleSelectListState<T> extends ListState<T> {
  /** The key for the currently selected item. */
  readonly selectedKey: Key;

  /** Sets the selected key. */
  setSelectedKey(key: Key | null): void;

  /** The value of the currently selected item. */
  readonly selectedItem: Node<T> | null;
}

/**
 * Provides state management for list-like components with single selection.
 * Handles building a collection of items from props, and manages selection state.
 */
export function useSingleSelectListState<T extends object>(
  props: SingleSelectListProps<T>,
): SingleSelectListState<T> {
  const [selectedKey, setSelectedKey] = useControlledState(
    props.selectedKey,
    props.defaultSelectedKey ?? null,
    props.onSelectionChange,
  );
  const selectedKeys = useMemo(
    () => (selectedKey != null ? [selectedKey] : []),
    [selectedKey],
  );
  const {collection, disabledKeys, selectionManager} = useListState({
    ...props,
    selectionMode: 'single',
    disallowEmptySelection: true,
    allowDuplicateSelectionEvents: true,
    selectedKeys,
    onSelectionChange: (keys) => {
      if (keys === 'all') {
        return;
      }

      const key = keys.values().next().value ?? null;

      // Always fire onSelectionChange, even if the key is the same
      // as the current key (useControlledState does not).
      if (key && key === selectedKey && props.onSelectionChange) {
        props.onSelectionChange(key);
      }

      setSelectedKey(key);
    },
  });

  const selectedItem =
    selectedKey != null ? collection.getItem(selectedKey) : null;

  return {
    collection,
    disabledKeys,
    selectionManager,
    selectedKey: selectedKey!,
    setSelectedKey,
    selectedItem,
  };
}
